import { NumericalCellValueStyle } from '@toggle/helpers';
import clsx from 'clsx';
import { Property } from 'csstype';
import React, { forwardRef, TableHTMLAttributes } from 'react';

import { WithNAFallback } from '~/components/table';

import styles from './TableCell.module.scss';

export type PinConfig = {
  left: number;
  right: number;
  isLastPinned: boolean;
};

export interface TableCellProps
  extends TableHTMLAttributes<HTMLTableCellElement> {
  alignment?: Property.TextAlign;
  condensed?: boolean;
  isNumber?: boolean;
  isUpperCase?: boolean;
  colspan?: number;
  fixed?: boolean;
  numericalValueStyle?: NumericalCellValueStyle;
  pinConfig?: PinConfig;
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  (
    {
      alignment = 'end',
      condensed,
      isNumber = true,
      isUpperCase = false,
      colspan,
      fixed,
      numericalValueStyle = NumericalCellValueStyle.Neutral,
      children,
      pinConfig,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    return (
      <td
        data-testid="table-cell"
        className={clsx(styles.tableCell, className, {
          [styles.isNumber]: isNumber,
          [styles.isUpperCase]: isUpperCase,
          [styles.positive]:
            numericalValueStyle === NumericalCellValueStyle.Positive,
          [styles.critical]:
            numericalValueStyle === NumericalCellValueStyle.Negative,
          [styles.fixed]: fixed,
          [styles.condensed]: condensed,
          [styles.pinned]: !!pinConfig,
          [styles.lastPinned]: !!pinConfig?.isLastPinned,
        })}
        style={{
          ...style,
          textAlign: alignment,
          ...(pinConfig && {
            left: `${pinConfig.left}px`,
            right: `${pinConfig.right}px`,
          }),
        }}
        colSpan={colspan}
        {...rest}
        ref={ref}
      >
        <WithNAFallback value={children} check={!!children} />
      </td>
    );
  }
);
